import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';

interface FaqItem { q: string; a: string }

export const HelpSupport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = i18n.language.startsWith('fr')
      ? 'Aide et soutien — AidFone'
      : 'Help & Support — AidFone';
  }, [i18n.language]);

  const faqItems = (t('help.faqItems', { returnObjects: true }) as FaqItem[]) ?? [];

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('help.title')}</h1>
          <p className="text-gray-500 mt-1">{t('help.subtitle')}</p>
        </div>

        {/* Contact Support Card — email only. No fake support phone. */}
        <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '20px' }}>support_agent</span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('help.contactSupport')}</h2>
              <p className="text-sm text-gray-600 mb-3">
                {t('help.supportHours')}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:support@aidfone.com"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-primary/90 transition-colors no-underline"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>email</span>
                  support@aidfone.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('help.faq')}</h2>
          <div className="space-y-2">
            {faqItems.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={i}
                  className="border border-gray-200 rounded-xl overflow-hidden bg-white"
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="w-full px-5 py-4 flex items-center justify-between text-left bg-transparent border-none cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-900 pr-4">{item.q}</span>
                    <span
                      className="material-symbols-outlined text-gray-400 flex-shrink-0 transition-transform duration-300 ease-out"
                      style={{ fontSize: '20px', transform: isOpen ? 'rotate(180deg)' : undefined }}
                    >
                      expand_more
                    </span>
                  </button>
                  {/* Grid-rows trick: grid-rows transitions from 0fr to 1fr for smooth height animation. */}
                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-4 pt-3 text-sm text-gray-600 leading-relaxed border-t border-gray-100">
                        {item.a}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('help.quickLinks')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href="/configure"
              className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-brand-primary/30 hover:bg-brand-primary/5 transition-colors no-underline"
            >
              <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '20px' }}>phone_android</span>
              <div>
                <div className="text-sm font-medium text-gray-900">{t('help.setupPhone')}</div>
                <div className="text-xs text-gray-500">{t('help.setupPhoneDesc')}</div>
              </div>
            </a>
            <a
              href="/dashboard"
              className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-brand-primary/30 hover:bg-brand-primary/5 transition-colors no-underline"
            >
              <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '20px' }}>dashboard</span>
              <div>
                <div className="text-sm font-medium text-gray-900">{t('help.goToDashboard')}</div>
                <div className="text-xs text-gray-500">{t('help.goToDashboardDesc')}</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HelpSupport;
